import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import "./styles.css";

/* =========================================================
   DATA
========================================================= */

const ladiesCategories = [
  {
    number: "01",
    title: "KURTAS",
    subtitle: "FOR WOMEN",
    image: "/ladies-kurtas-photo.png",
    path: "/ladies-wear/kurtas",
    alt: "Kurtas for women",
  },
  {
    number: "02",
    title: "KAFTAN MAXI DRESS",
    subtitle: "",
    image: "/ladies-kaftan-photo.png",
    path: "/ladies-wear/kaftan-maxi-dress",
    alt: "Kaftan maxi dress",
  },
  {
    number: "03",
    title: "ABAYAS",
    subtitle: "",
    image: "/ladies-abayas-photo.png",
    path: "/ladies-wear/abayas",
    alt: "Abayas",
  },
];

const uniformCategories = [
  {
    number: "01",
    title: "CORPORATE",
    image:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=900&q=85",
    path: "/uniforms/corporate",
    alt: "Corporate uniforms",
  },
  {
    number: "02",
    title: "SCHOOL",
    image:
      "https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=900&q=85",
    path: "/uniforms/school",
    alt: "School uniforms",
  },
  {
    number: "03",
    title: "HOSPITAL & MEDICAL",
    image:
      "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=900&q=85",
    path: "/uniforms/hospital-medical",
    alt: "Hospital and medical uniforms",
  },
  {
    number: "04",
    title: "HOTEL & HOSPITALITY",
    image:
      "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=85",
    path: "/uniforms/hotel-hospitality",
    alt: "Hotel and hospitality uniforms",
  },
  {
    number: "05",
    title: "INDUSTRIAL & WORKWEAR",
    image:
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=900&q=85",
    path: "/uniforms/industrial-workwear",
    alt: "Industrial and workwear",
  },
  {
    number: "06",
    title: "CUSTOM / OTHER",
    image:
      "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=900&q=85",
    path: "/uniforms/custom",
    alt: "Custom uniforms",
  },
];

/* =========================================================
   SCROLL TO TOP WHEN CHANGING PAGE
========================================================= */

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

/* =========================================================
   NAVBAR
========================================================= */

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <header className="navbar">
        <Link to="/" className="logo-image">
          <img
            src="https://res.cloudinary.com/wpzg88lf/image/upload/ChatGPT_Image_Sep_14_2026_07_21_20_PM.png"
            alt="Shabbasa Trading"
          />
        </Link>

        <div className="established">
          <span>ESTABLISHED 2001</span>
          <span>DUBAI · UAE</span>
        </div>

        <nav className="nav-links">
          <NavLink
            to="/"
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            HOME
          </NavLink>

          <NavLink
            to="/ladies-wear"
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            LADIES WEAR
          </NavLink>

          <NavLink
            to="/uniforms"
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            UNIFORMS
          </NavLink>

          <NavLink
            to="/about"
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            ABOUT
          </NavLink>

          <NavLink
            to="/contact"
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            CONTACT
          </NavLink>
        </nav>

        <Link to="/contact" className="nav-enquire">
          ENQUIRE
          <ArrowUpRight size={15} />
        </Link>

        <button
          className="menu-button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Open menu"
        >
          ☰
        </button>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <>
            {/* CLICKING THIS AREA CLOSES THE MENU */}
            <motion.div
              className="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={closeMenu}
            />

            {/* SIDEBAR */}
            <motion.div
              className="mobile-menu"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{
                duration: 0.45,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <div className="mobile-menu-inner">
                <div className="mobile-menu-label">
                  <span />
                  <p> SHABBASA / MENU</p>
                </div>

                <nav className="mobile-menu-links">
                  <Link to="/" onClick={closeMenu}>
                    <span>01</span>
                    HOME
                  </Link>

                  <Link to="/ladies-wear" onClick={closeMenu}>
                    <span>02</span>
                    LADIES WEAR
                  </Link>

                  <Link to="/uniforms" onClick={closeMenu}>
                    <span>03</span>
                    UNIFORMS
                  </Link>

                  <Link to="/about" onClick={closeMenu}>
                    <span>04</span>
                    ABOUT
                  </Link>

                  <Link to="/contact" onClick={closeMenu}>
                    <span>05</span>
                    CONTACT
                  </Link>
                </nav>

                <div className="mobile-menu-bottom">
                  <span>DUBAI · UAE</span>

                  <Link to="/contact" onClick={closeMenu}>
                    ENQUIRE
                    <ArrowUpRight size={17} />
                  </Link>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
/* =========================================================
   FOOTER
========================================================= */

function Footer() {
  return (
    <footer className="footer">
      {/* LOGO */}
      <div className="footer-logo">
        <Link to="/" className="footer-logo-image">
          <img
            src="https://res.cloudinary.com/wpzg88lf/image/upload/ChatGPT_Image_Sep_14_2026_07_21_20_PM.png"
            alt="Shabbasa Trading"
          />
        </Link>
      </div>

      {/* CONTACT */}
      <div className="footer-contact">
        <span className="footer-label">CONTACT</span>

        <a href="tel:+971553737967">+971 55 373 7967</a>

        <a href="tel:+971508449755">+971 50 844 9755</a>

        <a href="mailto:shabbasaa@yahoo.com">shabbasaa@yahoo.com</a>
      </div>

      {/* ADDRESS */}
      <div className="footer-address">
        <p>
          Rashidiya Airport Road,
          <br />
          Bin Sougat Centre, Shop M41
          <br />
          Dubai, UAE
        </p>
      </div>

      {/* BRAND */}
      <div className="footer-right">
        <span>SHABBASA</span>

        <span>TAILORING · ESTD. 2001</span>

        <small>© 2026 Shabbasa Trading LLC</small>
      </div>
    </footer>
  );
}

/* =========================================================
   HOME PAGE
========================================================= */

function Home() {
  const heroImages = [
    "https://res.cloudinary.com/wpzg88lf/image/upload/v1789584197/ChatGPT_Image_Sep_17_2026_12_12_52_AM.png",
    "https://res.cloudinary.com/wpzg88lf/image/upload/v1789581879/ChatGPT_Image_Sep_16_2026_11_32_05_PM.png",
    "https://res.cloudinary.com/wpzg88lf/image/upload/v1789584416/ChatGPT_Image_Sep_17_2026_12_16_35_AM.png",
    "https://res.cloudinary.com/wpzg88lf/image/upload/v1789585282/ChatGPT_Image_Sep_17_2026_12_28_22_AM.png",
  ];
  const [heroImage, setHeroImage] = useState(0);
  useEffect(() => {
    heroImages.forEach((imageSrc) => {
      const image = new Image();
      image.src = imageSrc;
    });
  }, []);
  return (
    <>
      <main className="hero" id="home">
        <div className="hero-grid" />
        <div className="hero-arc hero-arc-one" />
        <div className="hero-arc hero-arc-two" />

        <div className="hero-content">
          {/* rest of your hero content */}

          <motion.h1
            initial={{ opacity: 0, y: 35 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="heading-main">Tailoring</span>
            <span className="heading-script">with a pulse.</span>
          </motion.h1>

          <motion.p
            className="hero-description"
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
            }}
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            A Dubai-based tailoring house creating refined women’s wear and
            expertly made uniforms — where thoughtful design, quality fabrics,
            and precise craftsmanship come together.
          </motion.p>

          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <Link to="/ladies-wear" className="primary-button">
              EXPLORE LADIES' WEAR
              <ArrowUpRight size={16} />
            </Link>

            <Link to="/uniforms" className="secondary-button">
              EXPLORE UNIFORMS
              <ArrowUpRight size={16} />
            </Link>
          </motion.div>

          <div className="hero-stats">
            <div className="stat">
              <strong>20+</strong>
              <span>YEARS OF HERITAGE</span>
            </div>

            <div className="stat">
              <strong>03</strong>
              <span>LADIES' WEAR CATEGORIES</span>
            </div>

            <div className="stat">
              <strong>05</strong>
              <span>UNIFORM CATEGORIES</span>
            </div>
          </div>
        </div>

        <motion.div
          className="hero-photo"
          initial={{ opacity: 0, x: 45 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1 }}
        >
          <div className="photo-gold-frame" />

          <div className="photo-container">
            <img
              key={heroImage}
              src={heroImages[heroImage]}
              alt="Shabbasa clothing collection"
              className="hero-slider-image"
              loading="eager"
            />
            <button
              className="hero-slider-prev"
              onClick={() =>
                setHeroImage(
                  (heroImage - 1 + heroImages.length) % heroImages.length
                )
              }
              aria-label="Previous image"
            >
              ←
            </button>

            <button
              className="hero-slider-next"
              onClick={() => setHeroImage((heroImage + 1) % heroImages.length)}
              aria-label="Next image"
            >
              →
            </button>

            <div className="photo-overlay" />

            <div className="photo-label">
              <span>THE AL SHABBASA HOUSE / 01</span>
            </div>

            <div className="photo-caption">
              <span>A good garment</span>
              <em>changes the room.</em>
            </div>
          </div>
        </motion.div>

        <div className="side-message">
          <span>CLOTH</span>
          <span>PEOPLE</span>
          <span>PURPOSE</span>

          <div className="side-line" />

          <small>ESTD.</small>
          <small>2001</small>
        </div>
      </main>

      <section className="brand-strip">
        <div className="brand-track">
          <span>SHABBASA TAILORING</span>
          <b>—</b>
          <span>UNIFORMS MADE TO REPEAT</span>
          <b>—</b>
          <span>TEXTILES TRADED WITH CARE</span>
          <b>—</b>
          <span>READY-MADE LADIES' WEAR</span>
          <b>—</b>
          <span>DUBAI / UAE</span>
          <b>—</b>
          <span>SHABBASA TAILORING</span>
        </div>
      </section>

      {/* SMALL HOME PREVIEW */}
      <section className="home-preview">
        <div className="section-label">02 / EXPLORE</div>

        <div className="explore-cards-grid">
          <Link to="/ladies-wear" className="explore-card">
            <img src="/ladies-abayas-photo.png" alt="Ladies Wear" />

            <div className="explore-card-content">
              <span>01</span>

              <div>
                <h2>
                  Ladies
                  <br />
                  <i>Wear.</i>
                </h2>

                <p>Explore Kurtas, Kaftan Maxi Dress and Abayas.</p>
              </div>

              <ArrowUpRight size={24} />
            </div>
          </Link>

          <Link to="/uniforms" className="explore-card">
            <img
              src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=1200&q=85"
              alt="Uniform Manufacturing"
            />

            <div className="explore-card-content">
              <span>02</span>

              <div>
                <h2>
                  Uniform
                  <br />
                  <i>Manufacturing.</i>
                </h2>

                <p>Explore uniforms made for different industries.</p>
              </div>

              <ArrowUpRight size={24} />
            </div>
          </Link>
        </div>
      </section>
    </>
  );
}

/* =========================================================
   LADIES WEAR PAGE
========================================================= */

function LadiesWear() {
  return (
    <section className="ladies-section page-section">
      <div className="ladies-heading">
        <div className="section-label">02 / LADIES WEAR</div>

        <div className="ladies-title">
          <h2>
            Everyday
            <br />
            <i>elegance.</i>
          </h2>

          <p>
            Discover our collection of ready-made and customizable ladies wear,
            thoughtfully selected for comfort, style and everyday life.
          </p>
        </div>
      </div>

      <div className="ladies-categories ladies-page-grid">
        {ladiesCategories.map((item) => (
          <Link to={item.path} className="ladies-category" key={item.path}>
            <img src={item.image} alt={item.alt} />

            <div className="ladies-category-overlay" />

            <div className="ladies-category-info">
              <span>{item.number}</span>

              <div className="ladies-category-text">
                <h3
                  style={{
                    fontFamily: "Georgia, serif",
                    fontWeight: "normal",
                    letterSpacing: "0",
                  }}
                >
                  {item.title}
                </h3>
                {item.subtitle && <p>{item.subtitle}</p>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* =========================================================
   UNIFORMS PAGE
========================================================= */

function Uniforms() {
  return (
    <section className="uniform-section page-section">
      <div className="uniform-grid-bg" />

      <div className="uniform-hero">
        <div className="section-label">03 / UNIFORM MANUFACTURING</div>

        <h2>
          Built for <i>your people.</i>
        </h2>

        <p>
          From first sketch to finished garment, we manufacture uniforms
          designed around your team, your workplace and your brand identity.
        </p>

        <Link to="/contact" className="uniform-button">
          START A UNIFORM BRIEF
          <ArrowUpRight size={17} />
        </Link>
      </div>

      <div className="uniform-gallery">
        <div className="uniform-category-heading">
          <span>CHOOSE YOUR UNIFORM</span>
        </div>

        <div className="uniform-categories">
          {uniformCategories.map((item) => (
            <Link to={item.path} className="uniform-category" key={item.path}>
              <img src={item.image} alt={item.alt} />

              <div className="uniform-category-overlay" />

              <div className="uniform-category-info">
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <ArrowUpRight size={18} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   ABOUT PAGE
========================================================= */

function About() {
  return (
    <section className="about-section page-section">
      <div className="about-top">
        <div className="about-brand-block">
          <div className="section-label">04 / ABOUT SHABBASA TRADING LLC</div>

          <div className="about-image-logo">
            <img
              src="https://res.cloudinary.com/wpzg88lf/image/upload/ChatGPT_Image_Sep_14_2026_07_21_20_PM.png"
              alt="Shabbasa Trading"
            />
          </div>
        </div>

        <div className="about-intro">
          <h2>
            Made with
            <br />
            <i>intention.</i>
          </h2>

          <p className="about-main-description">
            Shabbasa Tailoring brings together ready-made ladies wear and
            professional uniform manufacturing under one name. With tailoring at
            the heart of what we do, we focus on garments that look right, feel
            right and are made for real people.
          </p>
        </div>
      </div>

      <div className="about-line" />

      <div className="about-content">
        <div className="about-year">
          <span>EST.</span>
          <strong>2001</strong>
          <small>DUBAI · UAE</small>
        </div>

        <div className="about-story">
          <span className="about-small-title">OUR APPROACH</span>

          <p>
            From everyday fashion to garments made for teams, we believe good
            clothing begins with understanding the person who will wear it.
          </p>

          <p>
            Every detail matters — from the choice of fabric and cut to the
            final finish. Our aim is simple: dependable clothing with a
            considered look.
          </p>
        </div>

        <div className="about-values">
          <div>
            <span>01</span>
            <h3>CRAFT</h3>
            <p>Attention to construction, fit and finish.</p>
          </div>

          <div>
            <span>02</span>
            <h3>QUALITY</h3>
            <p>Materials and garments selected with purpose.</p>
          </div>

          <div>
            <span>03</span>
            <h3>PEOPLE</h3>
            <p>Clothing designed around the people who wear it.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   CONTACT PAGE
========================================================= */

function Contact() {
  return (
    <section className="contact-section page-section">
      <div className="section-label">05 / CONTACT</div>

      <div className="contact-content">
        <div>
          <h1>
            Let's make
            <br />
            <i>something.</i>
          </h1>

          <p>
            Looking for ready-made ladies wear or uniforms for your team? Get in
            touch with Shabbasa Tailoring.
          </p>
        </div>

        <div className="contact-details">
          <div className="contact-item">
            <span>CALL NASSER</span>
            <a href="tel:+971553737967">
              +971 55 373 7967
              <ArrowUpRight size={17} />
            </a>
          </div>

          <div className="contact-item">
            <span>CALL RAFI</span>
            <a href="tel:+971508449755">
              +971 50 844 9755
              <ArrowUpRight size={17} />
            </a>
          </div>

          <div className="contact-item">
            <span>EMAIL</span>
            <a href="mailto:shabbasaa@yahoo.com">
              shabbasaa@yahoo.com
              <ArrowUpRight size={17} />
            </a>
          </div>

          <div className="contact-address">
            <span>VISIT US</span>

            <p>
              Rashidiya Airport Road,
              <br />
              Bin Sougat Centre, Shop M41,
              <br />
              Dubai, UAE
            </p>

            <a
              href="https://www.google.com/maps/search/?api=1&query=Bin+Sougat+Centre+Shop+M41+Rashidiya+Dubai+UAE"
              target="_blank"
              rel="noopener noreferrer"
              className="location-link"
            >
              <MapPin size={16} />
              GET DIRECTIONS
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   CATEGORY DETAIL PAGE
========================================================= */

function CategoryPage({ title, type }) {
  const isLadies = type === "ladies";
  const isKurtas = title.toLowerCase().includes("kurta");
  const isKaftans = title.toLowerCase().includes("kaftan");
  const isCorporate = title.toLowerCase().includes("corporate");
  const isSchool = title.toLowerCase().includes("school");
  const isHospital = title.toLowerCase().trim() === "hospital & medical";
  const isHotel = title.toLowerCase().includes("hotel");

  return (
    <section
      className={
        isLadies
          ? "category-detail ladies-detail page-section"
          : isHospital
          ? "category-detail uniform-detail hospital-layout page-section"
          : "category-detail uniform-detail page-section"
      }
    >
      <div className="section-label">
        {isLadies
          ? "LADIES WEAR / COLLECTION"
          : "UNIFORM MANUFACTURING / CATEGORY"}
      </div>

      <div
        className="category-detail-content"
        style={
          isHospital
            ? {
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                width: "100%",
              }
            : undefined
        }
      >
        <div>
          <h1>
            {title}
            <br />
          </h1>

          <p>
            More information about this collection will be available here. For
            enquiries, sizing, quantities or custom requirements, contact our
            team.
          </p>

          <Link to="/contact" className="uniform-button">
            MAKE AN ENQUIRY
            <ArrowUpRight size={17} />
          </Link>
        </div>
        {isCorporate && (
          <div className="corporate-gallery">
            <div className="corporate-gallery-item">
              <img
                src="https://res.cloudinary.com/wpzg88lf/image/upload/v1789586707/ChatGPT_Image_Sep_17_2026_12_53_07_AM.png"
                alt="Corporate Male Uniform"
              />
              <p>Corporate Male Uniform</p>
            </div>

            <div className="corporate-gallery-item">
              <img
                src="https://res.cloudinary.com/wpzg88lf/image/upload/v1789586738/ChatGPT_Image_Sep_17_2026_12_55_27_AM.png"
                alt="Corporate Female Uniform"
              />
              <p>Corporate Female Uniform</p>
            </div>
          </div>
        )}
      </div>

      {isSchool && (
        <div className="school-gallery">
          <div className="school-gallery-item">
            <img
              src="https://res.cloudinary.com/wpzg88lf/image/upload/v1789585868/ChatGPT_Image_Sep_17_2026_12_40_46_AM.png"
              alt="Boys school uniform"
            />
            <p>Boys Uniform</p>
          </div>

          <div className="school-gallery-item">
            <img
              src="https://res.cloudinary.com/wpzg88lf/image/upload/v1789586040/ChatGPT_Image_Sep_17_2026_12_43_29_AM.png"
              alt="Girls school uniform"
            />
            <p>Girls Uniform</p>
          </div>
        </div>
      )}

      {title === "Hospital & Medical" && (
        <div className="hospital-gallery">
          <div className="hospital-gallery-item">
            <img src="/hospital/doctors.png" alt="Doctor uniforms" />
            <p>Doctor Uniforms</p>
          </div>

          <div className="hospital-gallery-item">
            <img src="/hospital/nurse.png" alt="Nurse uniforms" />
            <p>Nurse Uniforms</p>
          </div>
        </div>
      )}
      {title === "Hotel & Hospitality" && (
        <div className="hotel-gallery">
          <div className="hotel-gallery-item">
            <img src="/hotel/bell-staff.png" alt="Hotel bell staff uniform" />
            <p>Bell Staff Uniform</p>
          </div>

          <div className="hotel-gallery-item">
            <img
              src="/hotel/housekeeping.png"
              alt="Hotel housekeeping uniform"
            />
            <p>Housekeeping Uniform</p>
          </div>
        </div>
      )}
      {title === "Industrial & Workwear" && (
        <div className="industrial-gallery">
          <div className="industrial-gallery-item">
            <img
              src="/industrial/coverall2.png"
              alt="Industrial coverall uniform"
            />
            <p>Industrial Coverall</p>
          </div>

          <div className="industrial-gallery-item">
            <img
              src="/industrial/workwear.png"
              alt="Industrial workwear uniform"
            />
            <p>Industrial Workwear</p>
          </div>
        </div>
      )}

      {/* KURTAS COLLECTION */}
      {title.toLowerCase().includes("abaya") && (
        <div className="abaya-gallery-new">
          <div className="abaya-gallery-new-item">
            <img src="/abaya/abaya.png" alt="Black embroidered abaya" />
            <p>Black Embroidered Abaya</p>
          </div>

          <div className="abaya-gallery-new-item">
            <img src="/abaya/abaya2.png" alt="Mauve embroidered abaya" />
            <p>Mauve Embroidered Abaya</p>
          </div>
        </div>
      )}
      {isKurtas && (
        <div className="collection-gallery">
          <div className="section-label">01 / KURTAS COLLECTION</div>

          <div className="collection-gallery-grid">
            <div className="collection-gallery-item">
              <img src="/kurtas-cover/kurta1.png" alt="Printed Kurta" />
              <p>Printed Kurta</p>
            </div>

            <div className="collection-gallery-item">
              <img src="/kurtas-cover/kurta2.png" alt="Embroidered Kurta" />
              <p>Embroidered Kurta</p>
            </div>

            <div className="collection-gallery-item">
              <img src="/kurtas-cover/lehenga.png" alt="Lehenga" />
              <p>Lehenga</p>
            </div>

            <div className="collection-gallery-item">
              <img src="/kurtas-cover/saree.png" alt="Saree" />
              <p>Saree</p>
            </div>
          </div>
        </div>
      )}

      {/* KAFTAN MAXI DRESS COLLECTION */}
      {isKaftans && (
        <div className="collection-gallery">
          <div className="section-label">01 / KAFTAN MAXI DRESS COLLECTION</div>

          <div className="collection-gallery-grid">
            <div className="collection-gallery-item">
              <img src="/kaftans/kaftan-1.png" alt="Floral kaftan maxi dress" />
              <p>Floral Kaftan Maxi Dress</p>
            </div>

            <div className="collection-gallery-item">
              <img src="/kaftans/kaftan-2.png" alt="Green embroidered kaftan" />
              <p>Green Embroidered Kaftan</p>
            </div>

            <div className="collection-gallery-item">
              <img
                src="/kaftans/kaftan-3.png"
                alt="Purple embroidered kaftan"
              />
              <p>Purple Embroidered Kaftan</p>
            </div>

            <div className="collection-gallery-item">
              <img
                src="/kaftans/kaftan-4.png"
                alt="Maroon embroidered kaftan"
              />
              <p>Maroon Embroidered Kaftan</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* =========================================================
   APP
========================================================= */

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />

      <div className="site">
        <Navbar />

        <Routes>
          <Route path="/" element={<Home />} />

          <Route path="/ladies-wear" element={<LadiesWear />} />

          <Route path="/uniforms" element={<Uniforms />} />

          <Route path="/about" element={<About />} />

          <Route path="/contact" element={<Contact />} />

          {/* LADIES WEAR DETAILS */}
          <Route
            path="/ladies-wear/kurtas"
            element={<CategoryPage title="Kurtas" type="ladies" />}
          />

          <Route
            path="/ladies-wear/kaftan-maxi-dress"
            element={<CategoryPage title="Kaftan Maxi Dress" type="ladies" />}
          />

          <Route
            path="/ladies-wear/abayas"
            element={<CategoryPage title="Abayas" type="ladies" />}
          />

          {/* UNIFORM DETAILS */}
          <Route
            path="/uniforms/corporate"
            element={<CategoryPage title="Corporate" type="uniform" />}
          />

          <Route
            path="/uniforms/school"
            element={<CategoryPage title="School" type="uniform" />}
          />

          <Route
            path="/uniforms/hospital-medical"
            element={<CategoryPage title="Hospital & Medical" type="uniform" />}
          />

          <Route
            path="/uniforms/hotel-hospitality"
            element={
              <CategoryPage title="Hotel & Hospitality" type="uniform" />
            }
          />

          <Route
            path="/uniforms/restaurant-cafe"
            element={<CategoryPage title="Restaurant & Café" type="uniform" />}
          />

          <Route
            path="/uniforms/security-staff"
            element={<CategoryPage title="Security & Staff" type="uniform" />}
          />

          <Route
            path="/uniforms/industrial-workwear"
            element={
              <CategoryPage title="Industrial & Workwear" type="uniform" />
            }
          />

          <Route
            path="/uniforms/custom"
            element={<CategoryPage title="Custom / Other" type="uniform" />}
          />

          {/* FALLBACK */}
          <Route
            path="*"
            element={<CategoryPage title="Page Not Found" type="uniform" />}
          />
        </Routes>

        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
